import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

// Per-user display preferences: theme (Dark / High contrast / Paper), density, rail collapsed.
// Stored in localStorage; applied as data-* attributes on <html> so tokens.css resolves them everywhere,
// including portals (toasts, dialogs).
const THEMES = ["dark", "dark-hc", "light"];
export const THEME_LABELS = { dark: "Dark", "dark-hc": "High contrast", light: "Paper" };

const read = (k, fallback) => {
  try { return localStorage.getItem(k) ?? fallback; } catch { return fallback; }
};
const write = (k, v) => {
  try { localStorage.setItem(k, v); } catch { /* private mode */ }
};

const PrefsCtx = createContext(null);
export const usePrefs = () => useContext(PrefsCtx);

export function PrefsProvider({ children }) {
  const [theme, setThemeState] = useState(() => (THEMES.includes(read("sentinel_theme")) ? read("sentinel_theme") : "dark"));
  const [density, setDensityState] = useState(() => (read("sentinel_density") === "compact" ? "compact" : "comfortable"));
  const [railCollapsed, setRailCollapsedState] = useState(() => read("sentinel_rail_collapsed") === "1");

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    // theme-color follows surface-base for the active theme.
    if (meta) meta.setAttribute("content", getComputedStyle(root).getPropertyValue("--surface-base").trim() || "black");
    write("sentinel_theme", theme);
  }, [theme]);
  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
    write("sentinel_density", density);
  }, [density]);
  useEffect(() => { write("sentinel_rail_collapsed", railCollapsed ? "1" : "0"); }, [railCollapsed]);

  // Print and PDF always use Paper.
  useEffect(() => {
    const root = document.documentElement;
    const before = () => root.setAttribute("data-theme", "light");
    const after = () => root.setAttribute("data-theme", theme);
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => { window.removeEventListener("beforeprint", before); window.removeEventListener("afterprint", after); };
  }, [theme]);

  const setTheme = useCallback((t) => setThemeState(THEMES.includes(t) ? t : "dark"), []);
  const cycleTheme = useCallback(() => setThemeState((t) => THEMES[(THEMES.indexOf(t) + 1) % THEMES.length]), []);
  const setDensity = useCallback((d) => setDensityState(d === "compact" ? "compact" : "comfortable"), []);
  const toggleDensity = useCallback(() => setDensityState((d) => (d === "compact" ? "comfortable" : "compact")), []);
  const toggleRail = useCallback(() => setRailCollapsedState((c) => !c), []);

  return (
    <PrefsCtx.Provider value={{ theme, setTheme, cycleTheme, density, setDensity, toggleDensity, railCollapsed, setRailCollapsed: setRailCollapsedState, toggleRail }}>
      {children}
    </PrefsCtx.Provider>
  );
}

/** "mobile" below 768px, "tablet" 768–1279 (rail collapsed), otherwise "desktop". */
export function useViewport() {
  const get = () => (typeof window === "undefined" ? 1440 : window.innerWidth);
  const [w, setW] = useState(get);
  useEffect(() => {
    const on = () => setW(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return { width: w, mobile: w < 768, narrow: w < 1280 };
}

/** Sets the document title to "Sentinel — <screen>". */
export function usePageTitle(screen) {
  useEffect(() => {
    document.title = screen ? `Sentinel — ${screen}` : "Sentinel";
  }, [screen]);
}
